import { ALL_LEVELS as AUTO_LEVELS } from './data';
import { MANUAL_LEVELS } from './manualData';
import type { LevelDef } from './types';
import type { Transmission } from '../sim/types';
import { CARS } from '../sim/cars';

export * from './types';

export const ALL_LEVELS: LevelDef[] = [...AUTO_LEVELS, ...MANUAL_LEVELS];

const byId = new Map<string, LevelDef>(ALL_LEVELS.map((l) => [l.id, l]));

export function getLevel(id: string): LevelDef {
  const level = byId.get(id);
  if (!level) throw new Error(`Nivel desconocido: ${id}`);
  return level;
}

export function orderedLevels(track?: Transmission): LevelDef[] {
  const list = track ? ALL_LEVELS.filter((l) => l.track === track) : ALL_LEVELS;
  return [...list].sort((a, b) => a.order - b.order);
}

/**
 * Valida la integridad de las definiciones de nivel. Devuelve la lista de
 * problemas encontrados (vacía si todo es correcto). Usado por tests y en dev.
 */
export function validateLevels(hasKey?: (key: string) => boolean): string[] {
  const problems: string[] = [];
  const ids = new Set<string>();
  const orders = new Set<number>();

  const checkKey = (key: string, where: string): void => {
    if (hasKey && !hasKey(key)) problems.push(`${where}: falta la clave i18n "${key}"`);
  };

  for (const level of ALL_LEVELS) {
    const where = `nivel ${level.id}`;
    if (ids.has(level.id)) problems.push(`${where}: id duplicado`);
    ids.add(level.id);
    if (orders.has(level.order)) problems.push(`${where}: order duplicado (${level.order})`);
    orders.add(level.order);
    const car = CARS[level.car];
    if (!car) problems.push(`${where}: coche desconocido "${level.car}"`);
    else if ((car.manual ? 'manual' : 'auto') !== level.track)
      problems.push(`${where}: el coche "${level.car}" no es del itinerario ${level.track}`);

    checkKey(level.titleKey, where);
    checkKey(level.descriptionKey, where);
    for (const tk of level.theoryKeys) {
      checkKey(`${tk}.title`, where);
      checkKey(`${tk}.body`, where);
    }

    const zoneIds = new Set(
      level.environment.props.filter((p) => p.type === 'zone').map((p) => (p as { id: string }).id),
    );
    const pathIds = new Set(Object.keys(level.environment.paths ?? {}));

    for (const obj of level.objectives) {
      const ow = `${where}, objetivo ${obj.id}`;
      checkKey(obj.titleKey, ow);
      if (obj.type === 'full_stop' && obj.zoneId && !zoneIds.has(obj.zoneId))
        problems.push(`${ow}: zona "${obj.zoneId}" no existe`);
      if (
        (obj.type === 'drive_to_zone' ||
          obj.type === 'reverse_to_zone' ||
          obj.type === 'hill_start') &&
        !zoneIds.has(obj.zoneId)
      )
        problems.push(`${ow}: zona "${obj.zoneId}" no existe`);
      if (
        (obj.type === 'bite_hold' || obj.type === 'move_off' || obj.type === 'hill_start') &&
        level.track !== 'manual'
      )
        problems.push(`${ow}: objetivo manual en un nivel automático`);
      if (obj.type === 'follow_path') {
        if (!pathIds.has(obj.pathId)) problems.push(`${ow}: trayectoria "${obj.pathId}" no existe`);
        if (!zoneIds.has(obj.toZoneId)) problems.push(`${ow}: zona "${obj.toZoneId}" no existe`);
      }
    }

    for (const unlock of level.unlocks) {
      if (unlock.startsWith('car:')) {
        if (!CARS[unlock.slice(4)]) problems.push(`${where}: desbloqueo de coche desconocido "${unlock}"`);
      } else if (!ALL_LEVELS.some((l) => l.id === unlock)) {
        problems.push(`${where}: desbloqueo de nivel desconocido "${unlock}"`);
      }
    }
  }
  return problems;
}
